<template>
  <div v-if="!loading && pages.length && !_.get(pages, '0.items', []).length" :class="['w-full sm:w-fit', isMain ? 'p-5 mt-5' : '']">
    <FeedFilterTags v-if="!isFavorite && !idSupplier && !routeName.startsWith('search___')" :type="CATEGORY_TYPE.CONTENT" :is-detail="isDetail" />
    <div class="w-full md:w-156 2xl:w-239 flex flex-col justify-center ml-1">
      <NotFound v-if="!isFavorite && !idSupplier && !routeName.startsWith('search___')" text="text-no-filtered-found" class="max-w-lg mx-auto" />
    </div>
  </div>
  <div v-else-if="!routeCategory.idCategory.value" id="contentView" :class="[ 'w-full sm:w-fit px-5', isMain ? 'mt-10 pb-5' : 'pb-20' ]">
    <FeedFilterTags v-if="!isFavorite && !idSupplier && !routeName.startsWith('search___')" :type="CATEGORY_TYPE.CONTENT" :is-detail="isDetail" />
    <h2 v-if="label && loaded" :class="['flex font-semibold text-xl mb-5', isDetail ? 'text-bolder' : 'text-heading']">
      {{ $t(label) }}
    </h2>
    <div v-if="!loaded || (loading && !filters.fulltext)" class="w-full sm:w-fit flex justify-center">
      <div class="w-full sm:w-fit grid md:grid-cols-2 2xl:grid-cols-3 gap-x-9 gap-y-2.5 sm:gap-y-9">
        <ContentFeedLoader :limit="6" />
      </div>
    </div>
    <div class="w-full sm:w-fit flex justify-center">
      <div class="w-full sm:w-fit grid md:grid-cols-2 2xl:grid-cols-3 gap-x-9 gap-y-2.5 sm:gap-y-9">
        <RenderContentCard
            v-for="(variant, idx) in variants"
            :key="variant.id"
            :variant="variant"
            :type="CONTENT_TYPE.INSPIRATION"
            :class="loaded ? '' : 'hidden'"
            :class-name="_.get(variant, 'status', STATUS.DRAFT) !== STATUS.ACTIVE ? 'opacity-50' : ''"
            :loading="idx < 3 ? null : 'lazy'"
        />
      </div>
    </div>
    <div v-if="hasNextPage || loadingMore" class="flex justify-center mt-8 lg:mt-12">
      <Button
          :variant="isDetail ? 'outline' : 'outlinePanel'"
          :loading="loadingMore"
          class-name="text-sm md:text-base font-semibold h-11"
          @click="handleLoadMore"
      >
          {{ $t("text-load-more") }}
      </Button>
    </div>
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { CATEGORY_TYPE, CONTENT_TYPE, PRODUCT_SUBTYPE, STATUS } from "@utils/constants";
import { useAuthStore } from '@stores/auth'
import { useSelectedStore } from '@stores/selected'
import { useSearchStore } from '@stores/search';
import { useContentStore } from '@stores/content';
import ContentFeedLoader from "@components/ui/loaders/content-feed-loader";
import RenderContentCard from "@components/content/render-content-card";
import Button from "@components/ui/button";
import NotFound from "@components/common/not-found";
import FeedFilterTags from "@components/filter/feed/feed-filter-tags";

const props = defineProps({
  idCategory: {
    type: Number,
    default: null
  },
  idSupplier: {
    type: Number,
    default: null
  },
  isFavorite: {
    type: Boolean,
    default: null
  },
  isMain: {
    type: Boolean,
    default: true
  },
  isDetail: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ''
  }
});

const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();

const auth = useAuthStore();
const store = useSelectedStore();
const search = useSearchStore();
const contents = useContentStore();
const route = useRoute();
const routeLang = useRouteLang();
const config = useRuntimeConfig();
const routeCategory = useRouteCategory();

const category = useState('route.category', () => config.public.layoutDesign);

const { filters } = useContentFilters(props);

const isPrivate = props.idSupplier || props.isFavorite;
const subtype = PRODUCT_SUBTYPE[(props.isFavorite ? PRODUCT_SUBTYPE.ifavorite : null) || (props.idSupplier ? PRODUCT_SUBTYPE.isupplier : null) || PRODUCT_SUBTYPE.inspiration] || PRODUCT_SUBTYPE.inspiration;
const bodyFilters = useState(`contents.${subtype}.filters.${category.value}`, () => {});
const pages = isPrivate
  ? ref([])
  : useState(`contents.${subtype}.pages.${category.value}`, () => []);
const pagination = isPrivate
  ? ref({ page: 1, pageCount: 1, itemsPerPage: 18, itemCount: 0 })
  : useState(`contents.${subtype}.pagination.${category.value}`, () => ({ page: 1, pageCount: 1, itemsPerPage: 18, itemCount: 0 }));
const loading = ref(false);
const loaded = ref(search.inspirationLoaded);
const hasNextPage = computed(() => pagination.value.page < pagination.value.pageCount);
const loadingMore = ref(false);
const replacePages = ref(false);

const variants = computed(() => loading.value && !pages.value.length
  ? []
  : _.flatMap(pages.value, (page) => page?.items || [])
);

const routeName = computed(() => route?.name || '');

const reload = _.debounce(async (pending = loading) => {
  pending.value = true;

  const body = _.pickBy(_.omit(filters.value, ['sort', 'direction']), value => value !== null && value !== '' && value !== 0);

  if (JSON.stringify(bodyFilters.value) !== JSON.stringify(body)) {
    replacePages.value = true;
    pagination.value.page = 1;
  }

  if (filters.value?.idSupplier === auth.idSupplier && !filters.value?.idCategory && !routeName.value.startsWith('supplier-supplier_id___')) {
    const { data, error, status } = await useApiFetch(API_ENDPOINTS.CONTENT_POST_MANAGE, {
      method: 'GET',
      params: {
        page: pagination.value.page,
        limit: pagination.value.itemsPerPage
      }
    });
    if (status.value === 'error') {
      if (import.meta.client && !auth.reloadTimeout) {
        $toast.error(t('error.content-detail.load'));
      }
      sentryCaptureEvent({
        message: 'content-feed.reload',
        level: 'error',
        extra: { error: error.value }
      });
    } else {
      bodyFilters.value = body;
      if (replacePages.value) {
        pages.value = [ data.value ];
      } else {
        pages.value.push(data.value);
      }
      pagination.value = data.value.pagination;
    }
  } else {
    const { status } = await loadContentInspiration(
      {
        type: CONTENT_TYPE.INSPIRATION,
        page: pagination.value.page,
        limit: pagination.value.itemsPerPage,
        sort: ['publishedAt', 'order'].includes(filters.value?.sort) ? filters.value?.sort : 'publishedAt',
        direction: filters.value?.direction || 'desc',
        locale: routeLang.locale.value
      },
      body
    );
    if (status.value === 'error') {
      if (import.meta.client && !auth.reloadTimeout) {
        $toast.error(t('error.content-detail.load'));
      }
    } else {
      const data = search.popInspiration();
      bodyFilters.value = body;
      if (replacePages.value) {
        pages.value = [ data.value ];
      } else {
        pages.value.push(data.value);
      }
      replacePages.value = false;
      pagination.value = data.value.pagination;
    }
  }
  pending.value = false;
  loaded.value = true;

  if (store.action === 'OPEN_FILTER_VIEW') {
    nextTick(() => {
      store.setAction(null);
      $eventBus.emit('sidebar:open', 'FILTER_VIEW');
    });
  }

  if (import.meta.client && filters.value?.fulltext) {
    scrollToView();
  }
}, 500, { leading: false, trailing: true });

const handleLoadMore = async () => {
  pagination.value.page++;
  await reload(loadingMore);
}

watch(() => store.loadReset, async values => {
  if (values[subtype] && (import.meta.server || navigator?.onLine)) {
    store.setLoadReset(subtype, false);
    replacePages.value = true;
    pagination.value.page = 1;
    await reload();
  }
}, { deep: true });

watch(() => route.query, async () => {
  if (loading.value || loadingMore.value) {
    return;
  }
  _.assignIn(filters.value, {
    idSupplier: +(props.idSupplier || route.query.idSupplier || null),
    idCategory: +(props.idCategory || route.query.idPostCategory || null),
    isFavorite: props.isFavorite,
    fulltext: route.query?.fulltext || null,
    sort: route.query?.sort === 'order' ? 'order' : null,
    direction: route.query?.direction || null,
  });

  if (import.meta.server || navigator?.onLine) {
    replacePages.value = true;
    pagination.value.page = 1;
    await reload();
  }
});

onServerPrefetch(async () => {
  pagination.value.page = 1;
  pages.value = [contents[CONTENT_TYPE.INSPIRATION]];
});

const contentUpdate = (value) => {
  const variant = _.find(variants.value, { id: value.id });
  if (variant) {
    _.assignIn(variant, value);
    store.setLoadResetOthers();
  }
};

onMounted(async () => {
  if (_.isEmpty(pages.value) || store.loadReset[subtype]) {
    store.setLoadReset(subtype, false);
  }
  if (import.meta.server || navigator?.onLine) {
    replacePages.value = true;
    pagination.value.page = 1;
    await reload();
  }
  $eventBus.on('content:update', contentUpdate);
});

onUnmounted(() => {
  $eventBus.off('content:update', contentUpdate);
});

</script>
